import React from 'react';
import {
	Alert,
	Button,
	Card,
	Col,
	Descriptions,
	Empty,
	Input,
	List,
	Row,
	Select,
	Space,
	Spin,
	Tag,
	Typography,
} from 'antd';
import useTenantsApi from './useTenantsApi';
import TenantAdminOnboardingCard from './TenantAdminOnboardingCard';

const { Title, Text } = Typography;
const { Option } = Select;

function getStatusColor(status) {
	switch (status) {
		case 'ready':
			return 'green';
		case 'failed':
			return 'red';
		case 'running':
			return 'blue';
		case 'pending':
		default:
			return 'gold';
	}
}

function formatTenantJobStatus(status) {
	if (!status) return 'Sin estado';
	const labels = {
		pending: 'Pendiente',
		running: 'Ejecutando',
		ready: 'Listo',
		failed: 'Fallido',
		canceled: 'Cancelado',
	};
	return labels[status] || status;
}

function renderStepDetails(details) {
	if (!details) return 'Sin detalles';
	if (typeof details === 'string') return details;
	try {
		return JSON.stringify(details, null, 2);
	} catch (error) {
		return 'No se pudieron serializar los detalles';
	}
}

function renderTenantErrorSummary(error) {
	if (!error) return 'Error no especificado';
	if (typeof error === 'string') return error;
	if (typeof error?.message === 'string' && error.message.trim()) {
		return error.message;
	}
	return 'El job registró un error técnico.';
}

export default function TenantsDisplay() {
	const {
		control,
		createForm,
		jobs,
		jobSteps,
		selectedJob,
		selectedJobSteps,
		onFormChange,
		onSubmit,
		onStatusFilterChange,
		onSelectJob,
		refresh,
		activeJobsCount,
	} = useTenantsApi();

	return (
		<div style={{ padding: 16, overflow: 'auto', height: '100%' }}>
			<Space direction='vertical' size='middle' style={{ width: '100%' }}>
				<Alert
					showIcon
					type='info'
					message='Módulo operacional de Tenants'
					description={`Ruta custom de Feed aislada del pipeline legacy. Jobs activos actuales: ${activeJobsCount}.`}
					action={
						<Button size='small' onClick={refresh}>
							Actualizar
						</Button>
					}
				/>

				<Row
					gutter={[16, 16]}
					style={{
						width: '100%',
						marginTop: 0,
						marginRight: 0,
						marginBottom: 0,
						marginLeft: 0,
					}}
				>
					<Col xs={24} xl={9}>
						<Card title='Crear tenant' bordered={false}>
							<Space
								direction='vertical'
								size='middle'
								style={{ width: '100%' }}
							>
								<Input
									value={createForm.companyName}
									placeholder='Company name'
									onChange={event =>
										onFormChange('companyName', event.target.value)
									}
								/>
								<Input
									value={createForm.organizationName}
									placeholder='Organization name'
									onChange={event =>
										onFormChange('organizationName', event.target.value)
									}
								/>
								<Input
									value={createForm.schemaSlug}
									placeholder='schema_slug'
									onChange={event =>
										onFormChange('schemaSlug', event.target.value)
									}
								/>
								<Row gutter={12}>
									<Col span={12}>
										<Input
											value={createForm.country}
											placeholder='País'
											onChange={event =>
												onFormChange('country', event.target.value)
											}
										/>
									</Col>
									<Col span={12}>
										<Input
											value={createForm.timezone}
											placeholder='Timezone'
											onChange={event =>
												onFormChange('timezone', event.target.value)
											}
										/>
									</Col>
								</Row>
								<Row gutter={12}>
									<Col span={8}>
										<Input
											value={createForm.adminName}
											placeholder='Admin name'
											onChange={event =>
												onFormChange('adminName', event.target.value)
											}
										/>
									</Col>
									<Col span={8}>
										<Input
											value={createForm.adminIdc}
											placeholder='IDC'
											onChange={event =>
												onFormChange('adminIdc', event.target.value)
											}
										/>
									</Col>
									<Col span={8}>
										<Input
											value={createForm.adminEmail}
											placeholder='admin@email.com'
											onChange={event =>
												onFormChange('adminEmail', event.target.value)
											}
										/>
									</Col>
								</Row>
								<Row gutter={12}>
									<Col span={12}>
										<Input
											value={createForm.baselineVersion}
											placeholder='Baseline version'
											disabled
										/>
									</Col>
									<Col span={12}>
										<Input
											value={(createForm.seedPacks || []).join(', ')}
											placeholder='Seed packs'
											disabled
										/>
									</Col>
								</Row>
								{createForm.error && (
									<Alert
										type='error'
										showIcon
										message='No se pudo crear el tenant'
										description={createForm.error}
									/>
								)}
								<Button
									type='primary'
									loading={createForm.isSubmitting}
									onClick={onSubmit}
								>
									Crear tenant
								</Button>
							</Space>
						</Card>
					</Col>

					<Col xs={24} xl={15}>
						<Card
							title='Jobs de provisioning'
							extra={
								<Select
									value={control.statusFilter}
									style={{ minWidth: 170 }}
									onChange={onStatusFilterChange}
								>
									<Option value='all'>Todos los estados</Option>
									<Option value='pending'>Pendiente</Option>
									<Option value='running'>Ejecutando</Option>
									<Option value='ready'>Listo</Option>
									<Option value='failed'>Fallido</Option>
								</Select>
							}
							bordered={false}
						>
							{control.isFetchingJobs ? (
								<div
									style={{
										display: 'flex',
										justifyContent: 'center',
										padding: 32,
									}}
								>
									<Spin />
								</div>
							) : jobs.error ? (
								<Alert type='error' showIcon message={jobs.error} />
							) : jobs.data.length === 0 ? (
								<Empty description='No hay jobs registrados' />
							) : (
								<List
									itemLayout='vertical'
									dataSource={jobs.data}
									renderItem={job => (
										<List.Item
											key={job.id}
											onClick={() => onSelectJob(job.id)}
											style={{
												cursor: 'pointer',
												padding: 16,
												borderRadius: 8,
												background:
													control.selectedJobId === job.id
														? 'rgba(24, 144, 255, 0.08)'
														: 'transparent',
											}}
										>
											<Space
												size='small'
												style={{
													width: '100%',
													justifyContent: 'space-between',
												}}
												wrap
											>
												<Space wrap>
													<Title
														level={5}
														style={{ marginTop: 0, marginBottom: 0 }}
													>
														#{job.id} · {job.schemaSlug}
													</Title>
													<Tag color={getStatusColor(job.status)}>
														{formatTenantJobStatus(job.status)}
													</Tag>
												</Space>
												<Text type='secondary'>
													{job.organizationId
														? `Org #${job.organizationId}`
														: 'Aún sin organization'}
												</Text>
											</Space>
											<Text type='secondary'>
												Creado: {job.createdAt || 'sin fecha'}
											</Text>
											{job.error && (
												<Alert
													type='error'
													showIcon
													style={{ marginTop: 12 }}
													message={renderTenantErrorSummary(job.error)}
													description={
														<pre
															style={{
																marginTop: 0,
																marginRight: 0,
																marginBottom: 0,
																marginLeft: 0,
																whiteSpace: 'pre-wrap',
																wordBreak: 'break-word',
															}}
														>
															{renderStepDetails(job.error)}
														</pre>
													}
												/>
											)}
										</List.Item>
									)}
								/>
							)}
						</Card>

						<Card
							title={
								selectedJob
									? `Detalle job #${selectedJob.id}`
									: 'Detalle del job seleccionado'
							}
							bordered={false}
							style={{ marginTop: 16 }}
						>
							{selectedJob ? (
								<Space
									direction='vertical'
									size='large'
									style={{ width: '100%' }}
								>
									<Descriptions column={2} size='small' bordered>
										<Descriptions.Item label='Schema'>
											{selectedJob.schemaSlug}
										</Descriptions.Item>
										<Descriptions.Item label='Estado'>
											<Tag color={getStatusColor(selectedJob.status)}>
												{formatTenantJobStatus(selectedJob.status)}
											</Tag>
										</Descriptions.Item>
										<Descriptions.Item label='Baseline'>
											{selectedJob.baselineVersion || 'v2'}
										</Descriptions.Item>
										<Descriptions.Item label='Solicitado por'>
											{selectedJob.requestedBy || 'N/A'}
										</Descriptions.Item>
										<Descriptions.Item label='Company ID'>
											{selectedJob.companyId || 'N/A'}
										</Descriptions.Item>
										<Descriptions.Item label='Organization ID'>
											{selectedJob.organizationId || 'N/A'}
										</Descriptions.Item>
										<Descriptions.Item label='Admin user ID'>
											{selectedJob.adminUserId || 'N/A'}
										</Descriptions.Item>
										<Descriptions.Item label='Main entity user ID'>
											{selectedJob.entityUserId || 'N/A'}
										</Descriptions.Item>
									</Descriptions>

									{selectedJob.error && (
										<Alert
											type='error'
											showIcon
											message={renderTenantErrorSummary(selectedJob.error)}
											description={
												<pre
													style={{
														marginTop: 0,
														marginRight: 0,
														marginBottom: 0,
														marginLeft: 0,
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-word',
													}}
												>
													{renderStepDetails(selectedJob.error)}
												</pre>
											}
										/>
									)}

									<div>
										<Title level={5}>Pasos</Title>
										{jobSteps.isFetching ? (
											<Spin />
										) : jobSteps.error ? (
											<Alert type='error' showIcon message={jobSteps.error} />
										) : selectedJobSteps.length === 0 ? (
											<Empty description='Este job aún no registra steps' />
										) : (
											<List
												dataSource={selectedJobSteps}
												renderItem={step => (
													<List.Item key={step.id}>
														<List.Item.Meta
															title={
																<Space wrap>
																	<Text strong>{step.stepKey}</Text>
																	<Tag color={getStatusColor(step.status)}>
																		{formatTenantJobStatus(step.status)}
																	</Tag>
																</Space>
															}
															description={
																<pre
																	style={{
																		marginTop: 0,
																		marginRight: 0,
																		marginBottom: 0,
																		marginLeft: 0,
																		whiteSpace: 'pre-wrap',
																		wordBreak: 'break-word',
																	}}
																>
																	{renderStepDetails(step.details)}
																</pre>
															}
														/>
													</List.Item>
												)}
											/>
										)}
									</div>
								</Space>
							) : (
								<Empty description='Selecciona un job para ver su detalle' />
							)}
						</Card>
					</Col>
				</Row>
				<TenantAdminOnboardingCard />
			</Space>
		</div>
	);
}
